/**
 * עיצוב והשלמת תאריכי DD/MM ללא ספריות חיצוניות.
 * תמיד מאחדים מחדש מ־ספרות (עד 8) — כך אחרי 15/06 המשך הקלדת 1985 הופך ל־15/06/1985
 * (ולא נתקעים ב־15/0685 כי נתיב "יש slash" לא עוצר עיצוב).
 */

/**
 * @param {string} value
 * @returns {string}
 */
export function formatDateInput(value) {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  return formatDigitsProgressive(digits);
}

/**
 * @param {string} digits — רק ספרות, עד 8
 * @returns {string}
 */
export function formatDigitsProgressive(digits) {
  const d = String(digits).replace(/\D/g, "");
  const len = d.length;
  if (len === 0) return "";
  const dd = d.slice(0, 2);
  if (len <= 2) return dd;
  const mm = d.slice(2, 4);
  if (len <= 4) return `${dd}/${mm}`;
  const yRest = d.slice(4, Math.min(8, len));
  return `${dd}/${mm}/${yRest}`;
}

/**
 * 00–29 → 2000–2029, 30–99 → 1930–1999
 * @param {string} yy שתי ספרות
 * @returns {number|null}
 */
export function expandTwoDigitYear(yy) {
  const s = String(yy).padStart(2, "0");
  if (s.length !== 2 || /\D/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n <= 29 ? 2000 + n : 1900 + n;
}

/**
 * @param {number} day
 * @param {number} month 1-12
 * @param {number} year
 * @returns {boolean}
 */
export function isValidCalendarDate(day, month, year) {
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year))
    return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(year, month - 1, day);
  return (
    dt.getFullYear() === year &&
    dt.getMonth() === month - 1 &&
    dt.getDate() === day
  );
}

function clampDayMonthPart(part) {
  const d = String(part).replace(/\D/g, "");
  if (!d) return "";
  const n = Number(d);
  if (!Number.isFinite(n)) return d;
  return String(Math.min(99, Math.max(0, n))).padStart(2, "0").slice(-2);
}

/**
 * נרמול אחרי blur ל־DD/MM/YYYY; שגיאה עדינה לתצוגה בלבד (לא חוסם הקלדה).
 * @param {string} value
 * @returns {{ normalized: string, error: string|null }}
 */
export function normalizeDateInputOnBlur(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return { normalized: "", error: null };

  if (raw.includes("/")) {
    const parts = raw.split("/").map((p) => p.trim());
    if (parts.length !== 3) {
      return { normalized: raw, error: "נא להשלים DD/MM/YYYY" };
    }
    let [a, b, c] = parts;
    const dayS = clampDayMonthPart(a);
    const monthS = clampDayMonthPart(b);
    const yearRaw = String(c).replace(/\D/g, "");
    if (!dayS || !monthS || !yearRaw) {
      return { normalized: raw, error: "תאריך לא מלא" };
    }

    let yearNum;
    if (yearRaw.length === 2) {
      yearNum = expandTwoDigitYear(yearRaw);
      if (yearNum == null) {
        return { normalized: raw, error: "שנה לא תקינה" };
      }
    } else if (yearRaw.length === 4) {
      yearNum = Number(yearRaw);
      if (!Number.isFinite(yearNum)) {
        return { normalized: raw, error: "שנה לא תקינה" };
      }
    } else {
      return { normalized: raw, error: "נא להזין שנה בת 2 או 4 ספרות" };
    }

    const day = Number(dayS);
    const month = Number(monthS);
    if (day < 1 || day > 31 || month < 1 || month > 12) {
      return {
        normalized: `${dayS}/${monthS}/${yearRaw}`,
        error: "יום או חודש לא תקינים",
      };
    }
    if (yearNum < 1900 || yearNum > 2100) {
      return {
        normalized: `${dayS}/${monthS}/${yearRaw}`,
        error: "שנה מחוץ לטווח",
      };
    }
    if (!isValidCalendarDate(day, month, yearNum)) {
      return {
        normalized: `${dayS}/${monthS}/${yearRaw}`,
        error: "תאריך לא קיים בלוח השנה",
      };
    }

    const normalized = `${dayS}/${monthS}/${yearNum}`;
    return { normalized, error: null };
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return { normalized: "", error: null };
  if (digits.length !== 6 && digits.length !== 8) {
    return {
      normalized: formatDigitsProgressive(digits),
      error: "נא 6 ספרות (DDMMYY) או 8 (DDMMYYYY)",
    };
  }

  const dayS = digits.slice(0, 2);
  const monthS = digits.slice(2, 4);
  const yearPart = digits.slice(4);
  const day = Number(dayS);
  const month = Number(monthS);
  let yearNum;
  if (yearPart.length === 2) {
    yearNum = expandTwoDigitYear(yearPart);
    if (yearNum == null) {
      return { normalized: formatDigitsProgressive(digits), error: "שנה לא תקינה" };
    }
  } else {
    yearNum = Number(yearPart);
  }

  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return {
      normalized: formatDigitsProgressive(digits),
      error: "יום או חודש לא תקינים",
    };
  }
  if (!Number.isFinite(yearNum) || yearNum < 1900 || yearNum > 2100) {
    return {
      normalized: formatDigitsProgressive(digits),
      error: "שנה מחוץ לטווח",
    };
  }
  if (!isValidCalendarDate(day, month, yearNum)) {
    return {
      normalized: formatDigitsProgressive(digits),
      error: "תאריך לא קיים בלוח השנה",
    };
  }

  const normalized = `${dayS}/${monthS}/${yearNum}`;
  return { normalized, error: null };
}
